import {useEffect, useState} from "react";
import * as React from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useBoundStore} from "../../../Store/store";
import {BaseCard} from "../../../Components/Cards/BaseCard";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import {PrimaryButton} from "../../../Components/Buttons/PrimaryButton";
import {useTranslation} from "react-i18next";
import {DangerButton} from "../../../Components/Buttons/DangerButton";
import {TextInput} from "../../../Components/Form/TextInput";
import {faArrowLeft} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {ContractsTable} from "../../../Components/Tables/TableCards/ContractsTable";
import {InvoicesTable} from "../../../Components/Tables/TableCards/InvoicesTable";
import {PerformanceReportsTable} from "../../../Components/Tables/TableCards/PerformanceReportsTable";
import {ShiftsTable} from "../../../Components/Tables/TableCards/ShiftsTable";
import {DeleteConfirmDialog} from "../../../Components/Dialogs/Confirmation/DeleteConfirmDialog";
import toast from "react-hot-toast";
import {SelectInput} from "../../../Components/Form/SelectInput";
import {TextAreaInput} from "../../../Components/Form/TextAreaInput";
import {Switch} from "@headlessui/react";
import {BranchPercentageCard} from "../../../Components/Cards/BranchPercentageCard";

export const AdminBranchDetails = () => {
    const {
        customerId,
        branchId,
    } = useParams();
    const navigate = useNavigate();
    const {t} = useTranslation();

    const getBranchDetail = useBoundStore((state) => state.getBranchDetail);
    const branchDetail = useBoundStore((state) => state.branchDetail);
    const deleteBranch = useBoundStore((state) => state.deleteBranch);
    const updateBranch = useBoundStore((state) => state.updateBranch);
    const markInvoiceAsPaid = useBoundStore((state) => state.markInvoiceAsPaid);
    const getAllContractTypes = useBoundStore((state) => state.getAllContractTypes);
    const contractTypes = useBoundStore((state) => state.contractTypes);

    const [openDeleteConfirm, setOpenDeleteConfirm] = React.useState(false);
    const [forceRenewData, setForceRenewData] = useState(false);
    const [branchLoading, setBranchLoading] = useState(true);
    const [hasInvoiceAddress, setHasInvoiceAddress] = useState(false);
    const [hasContractsDestination, setHasContractsDestination] = useState(false);
    const [hasPerformanceStatementDestination, setHasPerformanceStatementDestination] = useState(false);

    useEffect(() => {
        getAllContractTypes();
    }, [getAllContractTypes])

    const handleDelete = () => {
        deleteBranch(branchId).then(() => {
            setOpenDeleteConfirm(false);
            toast.success(t('branches.toast_deleted'));
            navigate('/admin/customers');
        })
    }

    const handleMarkAsPaid = async (invoiceId) => {
        const success = await markInvoiceAsPaid(invoiceId);
        if (success) {
            setForceRenewData(!forceRenewData);
        }
        return success;
    };

    useEffect(() => {
        const fetchBranchDetails = async () => {
            setBranchLoading(true);
            await getBranchDetail(branchId);
            setBranchLoading(false);
        };

        fetchBranchDetails();
    }, [getBranchDetail, branchId]);

    useEffect(() => {
        if (branchDetail?.invoice_email || branchDetail?.invoice_company_name || branchDetail?.invoice_vat_number) {
            setHasInvoiceAddress(true);
        } else {
            setHasInvoiceAddress(false);
        }

        if (branchDetail?.contracts_email || branchDetail?.contracts_firstname || branchDetail?.contracts_lastname) {
            setHasContractsDestination(true);
        } else {
            setHasContractsDestination(false);
        }

        if (branchDetail?.performancestatement_email || branchDetail?.performancestatement_firstname || branchDetail?.performancestatement_lastname) {
            setHasPerformanceStatementDestination(true);
        } else {
            setHasPerformanceStatementDestination(false);
        }
    }, [branchDetail]);

    useEffect(() => {
        if(branchDetail){
            reset({
                contact_firstname: branchDetail?.contact_firstname ?? null,
                contact_lastname: branchDetail?.contact_lastname ?? null,
                contact_email: branchDetail?.contact_email ?? null,
                contact_tel: branchDetail?.contact_tel ?? null,
                vat_number: branchDetail?.vat_number ?? null,
                company_name: branchDetail?.company_name ?? null,
                bank_account: branchDetail?.bank_account ?? null,
                street: branchDetail?.street ?? null,
                number: branchDetail?.number ?? null,
                bus: branchDetail?.bus ?? null,
                postal: branchDetail?.postal ?? null,
                city: branchDetail?.city ?? null,
                country: branchDetail?.country ?? null,
                province: branchDetail?.province ?? null,
                contract_type_id: branchDetail?.contract_type_id ?? null,
                max_hourly_rate_vk: branchDetail?.max_hourly_rate_vk ?? null,
                max_hourly_rate_zk: branchDetail?.max_hourly_rate_zk ?? null,
                alias: branchDetail?.alias ?? null,
                shift_confirmed_message: branchDetail?.shift_confirmed_message ?? null,
                invoice_email: branchDetail?.invoice_email ?? null,
                invoice_company_name: branchDetail?.invoice_company_name ?? null,
                invoice_vat_number: branchDetail?.invoice_vat_number ?? null,
                invoice_street: branchDetail?.invoice_street ?? null,
                invoice_number: branchDetail?.invoice_number ?? null,
                invoice_bus: branchDetail?.invoice_bus ?? null,
                invoice_city: branchDetail?.invoice_city ?? null,
                invoice_postal: branchDetail?.invoice_postal ?? null,
                contracts_email: branchDetail?.contracts_email ?? null,
                contracts_firstname: branchDetail?.contracts_firstname ?? null,
                contracts_lastname: branchDetail?.contracts_lastname ?? null,
                performancestatement_email: branchDetail?.performancestatement_email ?? null,
                performancestatement_firstname: branchDetail?.performancestatement_firstname ?? null,
                performancestatement_lastname: branchDetail?.performancestatement_lastname ?? null,
            })
        }
    }, [branchDetail]);

    const schema = yup.object({
        contact_firstname: yup.string().required().max(255),
        contact_lastname: yup.string().required().max(255),
        contact_email: yup.string().required().email().max(255),
        contact_tel: yup.string().required().max(255),
        vat_number: yup.string().required().max(255),
        company_name: yup.string().required().max(255),
        bank_account: yup.string().nullable().max(255),
        street: yup.string().required().max(255),
        number: yup.string().required().max(255),
        bus: yup.string().nullable().max(255),
        postal: yup.string().required().max(255),
        city: yup.string().required().max(255),
        country: yup.string().required().max(255),
        province: yup.string()
            .required()
            .oneOf([
                'antwerpen', 'limburg', 'oost-vlaanderen', 'vlaams-brabant', 'west-vlaanderen',
                'henegouwen', 'luik', 'luxemburg', 'namen', 'waals-brabant', 'brussels-hoofdstedelijk-gewest'
            ]),
        contract_type_id: yup.string().nullable(),
        alias: yup.string().nullable(),
        shift_confirmed_message: yup.string().nullable(),
        max_hourly_rate_vk: yup.number().nullable().transform((value, originalValue) => originalValue === '' ? null : value),
        max_hourly_rate_zk: yup.number().nullable().transform((value, originalValue) => originalValue === '' ? null : value),
        invoice_email: hasInvoiceAddress ? yup.string().required().email().max(255) : yup.string().nullable().max(255),
        invoice_company_name: hasInvoiceAddress ? yup.string().required().max(255) : yup.string().nullable().max(255),
        invoice_vat_number: hasInvoiceAddress ? yup.string().required().max(255) : yup.string().nullable().max(255),
        invoice_street: hasInvoiceAddress ? yup.string().required().max(255) : yup.string().nullable().max(255),
        invoice_number: hasInvoiceAddress ? yup.string().required().max(255) : yup.string().nullable().max(255),
        invoice_bus: yup.string().nullable().max(255),
        invoice_city: hasInvoiceAddress ? yup.string().required().max(255) : yup.string().nullable().max(255),
        invoice_postal: hasInvoiceAddress ? yup.string().required().max(255) : yup.string().nullable().max(255),
        contracts_email: hasContractsDestination ? yup.string().required().email().max(255) : yup.string().nullable().max(255),
        contracts_firstname: hasContractsDestination ? yup.string().required().max(255) : yup.string().nullable().max(255),
        contracts_lastname: hasContractsDestination ? yup.string().required().max(255) : yup.string().nullable().max(255),
        performancestatement_email: hasPerformanceStatementDestination ? yup.string().required().email().max(255) : yup.string().nullable().max(255),
        performancestatement_firstname: hasPerformanceStatementDestination ? yup.string().required().max(255) : yup.string().nullable().max(255),
        performancestatement_lastname: hasPerformanceStatementDestination ? yup.string().required().max(255) : yup.string().nullable().max(255),
    }).required()

    const {
        register,
        handleSubmit,
        formState: {errors},
        getValues,
        setValue,
        control,
        reset
    } = useForm({
        resolver: yupResolver(schema),
        reValidateMode: 'onBlur',
        mode: 'onChange',
        defaultValues:{
            contact_firstname: branchDetail?.contact_firstname ?? null,
            contact_lastname: branchDetail?.contact_lastname ?? null,
            contact_email: branchDetail?.contact_email ?? null,
            contact_tel: branchDetail?.contact_tel ?? null,
            vat_number: branchDetail?.vat_number ?? null,
            company_name: branchDetail?.company_name ?? null,
            bank_account: branchDetail?.bank_account ?? null,
            street: branchDetail?.street ?? null,
            number: branchDetail?.number ?? null,
            bus: branchDetail?.bus ?? null,
            postal: branchDetail?.postal ?? null,
            city: branchDetail?.city ?? null,
            country: branchDetail?.country ?? null,
            province: branchDetail?.province ?? null,
            contract_type_id: branchDetail?.contract_type_id ?? null,
            max_hourly_rate_vk: branchDetail?.max_hourly_rate_vk ?? null,
            max_hourly_rate_zk: branchDetail?.max_hourly_rate_zk ?? null,
            alias: branchDetail?.alias ?? null,
            shift_confirmed_message: branchDetail?.shift_confirmed_message ?? null,
            invoice_email: branchDetail?.invoice_email ?? null,
            invoice_company_name: branchDetail?.invoice_company_name ?? null,
            invoice_vat_number: branchDetail?.invoice_vat_number ?? null,
            invoice_street: branchDetail?.invoice_street ?? null,
            invoice_number: branchDetail?.invoice_number ?? null,
            invoice_bus: branchDetail?.invoice_bus ?? null,
            invoice_city: branchDetail?.invoice_city ?? null,
            invoice_postal: branchDetail?.invoice_postal ?? null,
            contracts_email: branchDetail?.contracts_email ?? null,
            contracts_firstname: branchDetail?.contracts_firstname ?? null,
            contracts_lastname: branchDetail?.contracts_lastname ?? null,
            performancestatement_email: branchDetail?.performancestatement_email ?? null,
            performancestatement_firstname: branchDetail?.performancestatement_firstname ?? null,
            performancestatement_lastname: branchDetail?.performancestatement_lastname ?? null,
        }
    });

    const onSubmit = (data) => {
        const submitData = hasInvoiceAddress ? data : {
            ...data,
            invoice_email: null,
            invoice_company_name: null,
            invoice_vat_number: null,
            invoice_street: null,
            invoice_number: null,
            invoice_bus: null,
            invoice_city: null,
            invoice_postal: null,
        };

        const finalData = hasContractsDestination ? submitData : {
            ...submitData,
            contracts_email: null,
            contracts_firstname: null,
            contracts_lastname: null,
        };

        const completeData = hasPerformanceStatementDestination ? finalData : {
            ...finalData,
            performancestatement_email: null,
            performancestatement_firstname: null,
            performancestatement_lastname: null,
        };

        updateBranch(branchId, completeData).then(() => {
            toast.success(t('branches.toast_updated'));
        });
    }

    if (branchLoading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div>
            <div>
                <div className={'flex flex-row justify-between mb-4 items-center'}>
                    <div>
                        <h1 className={'text-2xl font-semibold'}>{t('branches.detail_title')}</h1>
                        <div className={"flex flex-row group items-center"} onClick={() => {
                            navigate(-1);
                        }}>
                            <FontAwesomeIcon icon={faArrowLeft}
                                                  className={'text-sm transition-all group-hover:mr-2 duration-500 cursor-pointer'}/>

                            <div className={'text-sm text-gray-500 my-auto cursor-pointer ml-2'}>{t('general.actions.back_to_overview')}</div>
                        </div>
                    </div>
                </div>
            </div>
            <div className={'grid grid-cols-1 md:grid-cols-3 md:space-x-4 space-y-4 md:space-y-0'}>
                <div className={'md:col-span-3 space-y-4'}>
                    <BaseCard border={false}>
                        <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
                            <div className={'flex flex-col md:flex-row justify-between'}>
                                <h2 className={'text-lg text-gray-700 uppercase mb-1 mt-2'}>{t('branches.info_title')}</h2>
                            </div>

                            <div className={'grid grid-cols-1 md:grid-cols-2 gap-4'}>
                                <TextInput
                                    name={'contact_firstname'}
                                    register={register}
                                    translationIndex={'general.fields.contact_firstname'}
                                    errors={errors}
                                />
                                <TextInput
                                    name={'contact_lastname'}
                                    register={register}
                                    translationIndex={'general.fields.contact_lastname'}
                                    errors={errors}
                                />
                            </div>
                            <div className={'grid grid-cols-1 md:grid-cols-2 gap-4'}>
                                <TextInput
                                    name={'contact_email'}
                                    register={register}
                                    translationIndex={'general.fields.contact_email'}
                                    errors={errors}
                                />
                                <TextInput
                                    name={'contact_tel'}
                                    register={register}
                                    translationIndex={'general.fields.contact_tel'}
                                    errors={errors}
                                />
                            </div>
                            <div className={'grid grid-cols-1 md:grid-cols-2 gap-4'}>
                                <TextInput
                                    name={'vat_number'}
                                    register={register}
                                    translationIndex={'general.fields.vat_number'}
                                    errors={errors}
                                />
                                <TextInput
                                    name={'company_name'}
                                    register={register}
                                    translationIndex={'general.fields.company_name'}
                                    errors={errors}
                                />
                            </div>
                            <div className={'grid grid-cols-1 md:grid-cols-2 gap-4'}>
                                <TextInput
                                    name={'alias'}
                                    register={register}
                                    translationIndex={t('branches.alias')}
                                    errors={errors}
                                />
                                <TextInput
                                    name={'bank_account'}
                                    register={register}
                                    translationIndex={'general.fields.bank_account'}
                                    errors={errors}
                                />
                            </div>
                            <div className={'grid grid-cols-1 md:grid-cols-3 gap-4'}>
                                <TextInput
                                    name={'street'}
                                    register={register}
                                    translationIndex={'general.fields.street'}
                                    errors={errors}
                                />
                                <TextInput
                                    name={'number'}
                                    register={register}
                                    translationIndex={'general.fields.number'}
                                    errors={errors}
                                />
                                <TextInput
                                    name={'bus'}
                                    register={register}
                                    translationIndex={'general.fields.bus'}
                                    errors={errors}
                                />
                            </div>
                            <div className={'grid grid-cols-1 md:grid-cols-2 gap-4'}>
                                <TextInput
                                    name={'postal'}
                                    register={register}
                                    translationIndex={'general.fields.postal'}
                                    errors={errors}
                                />
                                <TextInput
                                    name={'city'}
                                    register={register}
                                    translationIndex={'general.fields.city'}
                                    errors={errors}
                                />
                            </div>
                            <div className={'grid grid-cols-1 md:grid-cols-2 gap-4'}>
                                <TextInput
                                    name={'country'}
                                    register={register}
                                    translationIndex={'general.fields.country'}
                                    errors={errors}
                                />
                                <SelectInput
                                    name={'province'}
                                    options={[
                                        { value: 'antwerpen', label: t('provinces.antwerp') },
                                        { value: 'limburg', label: t('provinces.limburg') },
                                        { value: 'oost-vlaanderen', label: t('provinces.east_flanders') },
                                        { value: 'vlaams-brabant', label: t('provinces.flemish_brabant') },
                                        { value: 'west-vlaanderen', label: t('provinces.west_flanders') },
                                        { value: 'henegouwen', label: t('provinces.hainaut') },
                                        { value: 'luik', label: t('provinces.liege') },
                                        { value: 'luxemburg', label: t('provinces.luxembourg') },
                                        { value: 'namen', label: t('provinces.namur') },
                                        { value: 'waals-brabant', label: t('provinces.walloon_brabant') },
                                        { value: 'brussels-hoofdstedelijk-gewest', label: t('provinces.brussels') },
                                    ]}
                                    setValue={setValue}
                                    control={control}
                                    value={getValues('province') ?? null}
                                    placeholder={t('general.fields.province')}
                                    translationIndex={'general.fields.province'}
                                />
                            </div>

                            <div className="mb-2">
                                <TextAreaInput
                                    register={register}
                                    name={'shift_confirmed_message'}
                                    translationIndex={t('branches.additional_info') + ' (' + t('branches.additional_info_warning') + ')'}
                                    rows={5}
                                    errors={errors}
                                />
                            </div>

                            <div className="mb-4 p-3 bg-gray-100 rounded-lg border border-gray-200">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-900">
                                        {t('branches.has_different_invoice_address')}
                                    </span>
                                    <Switch
                                        checked={hasInvoiceAddress}
                                        onChange={setHasInvoiceAddress}
                                        className={`${
                                            hasInvoiceAddress ? 'bg-primary-600' : 'bg-gray-300'
                                        } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2`}
                                    >
                                        <span
                                            className={`${
                                                hasInvoiceAddress ? 'translate-x-6' : 'translate-x-1'
                                            } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                                        />
                                    </Switch>
                                </div>
                            </div>

                            {hasInvoiceAddress && (
                                <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                    <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('branches.invoice_address')}</h3>
                                    <div className={'grid grid-cols-1 md:grid-cols-2 gap-4'}>
                                        <TextInput
                                            register={register}
                                            name={'invoice_email'}
                                            translationIndex={t('branches.invoice_email')}
                                            errors={errors}
                                        />
                                        <TextInput
                                            register={register}
                                            name={'invoice_company_name'}
                                            translationIndex={t('branches.invoice_company')}
                                            errors={errors}
                                        />
                                    </div>
                                    <div className="mb-2">
                                        <TextInput
                                            register={register}
                                            name={'invoice_vat_number'}
                                            translationIndex={t('branches.invoice_vat')}
                                            errors={errors}
                                        />
                                    </div>
                                    <div className={'grid grid-cols-1 md:grid-cols-3 gap-4'}>
                                        <TextInput
                                            register={register}
                                            name={'invoice_street'}
                                            translationIndex={t('branches.invoice_street')}
                                            errors={errors}
                                        />
                                        <TextInput
                                            register={register}
                                            name={'invoice_number'}
                                            translationIndex={t('branches.invoice_number')}
                                            errors={errors}
                                        />
                                        <TextInput
                                            register={register}
                                            name={'invoice_bus'}
                                            translationIndex={t('branches.invoice_bus')}
                                            errors={errors}
                                        />
                                    </div>
                                    <div className={'grid grid-cols-1 md:grid-cols-2 gap-4'}>
                                        <TextInput
                                            register={register}
                                            name={'invoice_postal'}
                                            translationIndex={t('branches.invoice_postal')}
                                            errors={errors}
                                        />
                                        <TextInput
                                            register={register}
                                            name={'invoice_city'}
                                            translationIndex={t('branches.invoice_city')}
                                            errors={errors}
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="mb-4 p-3 bg-gray-100 rounded-lg border border-gray-200">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-900">
                                        {t('branches.has_different_contracts_destination')}
                                    </span>
                                    <Switch
                                        checked={hasContractsDestination}
                                        onChange={setHasContractsDestination}
                                        className={`${
                                            hasContractsDestination ? 'bg-primary-600' : 'bg-gray-300'
                                        } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2`}
                                    >
                                        <span
                                            className={`${
                                                hasContractsDestination ? 'translate-x-6' : 'translate-x-1'
                                            } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                                        />
                                    </Switch>
                                </div>
                            </div>

                            {hasContractsDestination && (
                                <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                    <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('branches.contracts_destination')}</h3>
                                    <div className={'grid grid-cols-1 md:grid-cols-2 gap-4'}>
                                        <TextInput
                                            register={register}
                                            name={'contracts_email'}
                                            translationIndex={t('branches.contract_email')}
                                            errors={errors}
                                        />
                                        <TextInput
                                            register={register}
                                            name={'contracts_firstname'}
                                            translationIndex={t('branches.contract_firstname')}
                                            errors={errors}
                                        />
                                    </div>
                                    <div className="mb-2">
                                        <TextInput
                                            register={register}
                                            name={'contracts_lastname'}
                                            translationIndex={t('branches.contract_lastname')}
                                            errors={errors}
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="mb-4 p-3 bg-gray-100 rounded-lg border border-gray-200">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-900">
                                        {t('branches.has_different_performance_destination')}
                                    </span>
                                    <Switch
                                        checked={hasPerformanceStatementDestination}
                                        onChange={setHasPerformanceStatementDestination}
                                        className={`${
                                            hasPerformanceStatementDestination ? 'bg-primary-600' : 'bg-gray-300'
                                        } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2`}
                                    >
                                        <span
                                            className={`${
                                                hasPerformanceStatementDestination ? 'translate-x-6' : 'translate-x-1'
                                            } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                                        />
                                    </Switch>
                                </div>
                            </div>

                            {hasPerformanceStatementDestination && (
                                <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                    <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('branches.performance_destination')}</h3>
                                    <div className={'grid grid-cols-1 md:grid-cols-2 gap-4'}>
                                        <TextInput
                                            register={register}
                                            name={'performancestatement_email'}
                                            translationIndex={t('branches.performance_email')}
                                            errors={errors}
                                        />
                                        <TextInput
                                            register={register}
                                            name={'performancestatement_firstname'}
                                            translationIndex={t('branches.performance_firstname')}
                                            errors={errors}
                                        />
                                    </div>
                                    <div className="mb-2">
                                        <TextInput
                                            register={register}
                                            name={'performancestatement_lastname'}
                                            translationIndex={t('branches.performance_lastname')}
                                            errors={errors}
                                        />
                                    </div>
                                </div>
                            )}

                            <div className={'mt-4'}>
                                <PrimaryButton onClick={handleSubmit(onSubmit)}>
                                    {t('branches.save_branch')}
                                </PrimaryButton>
                            </div>
                        </form>
                    </BaseCard>

                    <BranchPercentageCard branchId={branchId} />

                    <BaseCard border={false}>
                        <h2 className={'text-lg text-gray-700 uppercase mb-1 mt-2'}>{t('shifts.title')}</h2>
                        <ShiftsTable userId={customerId} branchId={branchId} forceNewData={forceRenewData}/>
                    </BaseCard>

                    <BaseCard border={false}>
                        <h2 className={'text-lg text-gray-700 uppercase mb-1 mt-2'}>{t('contracts.title')}</h2>
                        <ContractsTable
                            userId={customerId}
                            branchId={branchId}
                            forceNewData={forceRenewData}
                            showGenerateNewButton={true}
                        />
                    </BaseCard>

                    <BaseCard border={false}>
                    <h2 className={'text-lg text-gray-700 uppercase mb-1 mt-2'}>{t('invoices.title')}</h2>
                        <InvoicesTable userId={customerId} branchId={branchId} showActions={true} onMarkAsPaid={handleMarkAsPaid} forceNewData={forceRenewData}/>
                    </BaseCard>

                    <BaseCard border={false}>
                        <h2 className={'text-lg text-gray-700 uppercase mb-1 mt-2'}>{t('performance_reports.title')}</h2>
                        <PerformanceReportsTable userId={customerId} branchId={branchId} forceNewData={forceRenewData}/>
                    </BaseCard>
                </div>
            </div>

            <DeleteConfirmDialog open={openDeleteConfirm} onClose={() => setOpenDeleteConfirm(false)} onConfirm={handleDelete}/>
        </div>
    )
}

